import React, { useEffect, useState } from "react";
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Box,
  Chip,
} from "@material-ui/core";

import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import { makeStyles } from "@material-ui/core/styles";
import styles from "assets/jss/material-dashboard-react/views/dashboardStyle.js";
import { fetchLeadsOfEmployee } from "views/Apis/AfterLogin";
import { getAllServices } from "views/Apis/AfterLogin";
import { updateLeadStatus } from "views/Apis/AfterLogin";
import { getAllSubServicesBasedOnServiceId } from "views/Apis/AfterLogin";

const callStatusOptions = [
  "ANSWERED",
  "NOT_ANSWERED",
  "DISCONNECTING",
  "NOT_REACHABLE",
  "OUT_OF_NETWORK",
  "WRONG_NUMBER",
];

const leadStatusOptions = [
  "INTERESTED",
  "NOT_INTERESTED",
  "FOLLOW_UP_NEEDED",
];

// Static lead remarks options
const leadRemarksOptions = [
  "Awaiting Required Documents from Client",
  "Client Requested Postponement",
  "Client to Confirm Service Continuation",
  "Closed",
  "Completed – Filings Done, Payment Received, Acknowledgement Available",
  "Completed with Partial Payment Received",
  "Delayed Due to Government Portal Issues",
  "Documents Received – Filing in Progress",
  "Filed Late with Penalty – Client Acknowledged",
  "Follow-up Required – No Response from Client",
  "Not Interested in Continuing Services",
  "Onboarding in Progress – KYC/POA Pending",
  "Pending Payment – Invoice Shared",
  "Recurring Service – Scheduled for Next Cycle",
  "Service Cancelled by Client",
  "Service Initiated – Documentation Under Process",
  "Service Paused Temporarily",
  "Submitted – Awaiting Acknowledgement from Department",
  "Switched Vendor / Service Provider Changed",
  "Under Review – Compliance Check Ongoing"
];

const useStyles = makeStyles((theme) => ({
  ...styles,
  tableHeadCell: {
    fontWeight: "bold",
    backgroundColor: "#e3f2fd",
    color: "#0d47a1",
  },
  statusBadge: {
    padding: "4px 8px",
    borderRadius: "4px",
    backgroundColor: "#f1f8e9",
    color: "#558b2f",
    fontSize: "12px",
    fontWeight: 500,
    textAlign: "center",
    display: "inline-block",
    minWidth: 60,
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
    alignItems: "center",
  },
  topHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  multiSelectValue: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
  },
  // Enhanced dialog styles
  dialogPaper: {
    borderRadius: 12,
    minHeight: 400,
    maxHeight: '85vh',
  },
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(2),
    '& .MuiTypography-h6': {
      fontWeight: 600,
      fontSize: '1.1rem',
    }
  },
  dialogContent: {
    padding: theme.spacing(2),
    '& .MuiTextField-root': {
      marginBottom: theme.spacing(1.5),
    }
  },
  dialogActions: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(1),
  },
  leadInfo: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(1.5),
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  compactFormField: {
    '& .MuiInputLabel-root': {
      fontSize: '0.9rem',
    },
    '& .MuiInputBase-input': {
      padding: '12px 14px',
    },
  },
  serviceChip: {
    margin: theme.spacing(0.25),
    height: 24,
    fontSize: '0.75rem',
    '& .MuiChip-deleteIcon': {
      color: theme.palette.error.main,
      fontSize: '16px',
      '&:hover': {
        color: theme.palette.error.dark,
      }
    }
  },
  subServiceSection: {
    backgroundColor: theme.palette.grey[25],
    padding: theme.spacing(1.5),
    borderRadius: 6,
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[100]}`,
  },
  leadRemarksSection: {
    backgroundColor: theme.palette.info.light,
    padding: theme.spacing(1.5),
    borderRadius: 6,
    marginTop: theme.spacing(1.5),
    border: `1px solid ${theme.palette.info.main}`,
  },
}));

export default function GetLeadsEmployeeView() {
  const classes = useStyles();
  const [leads, setLeads] = useState([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const employeeId = sessionStorage.getItem("userId");

  // Service related states
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [serviceSubServicesMap, setServiceSubServicesMap] = useState({});
  const [selectedSubServicesMap, setSelectedSubServicesMap] = useState({});

  // Form states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [callStatus, setCallStatus] = useState("");
  const [leadStatus, setLeadStatus] = useState("");
  const [comment, setComment] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [leadRemarks, setLeadRemarks] = useState(""); // New state for lead remarks

  // Fetch sub-services for a specific service
  const fetchSubServices = async (serviceId) => {
    try {
      const data = await getAllSubServicesBasedOnServiceId(serviceId);
      setServiceSubServicesMap(prev => ({
        ...prev,
        [serviceId]: data
      }));
      console.log("Fetched sub-services for service:", serviceId, data);
    } catch (error) {
      console.error("Failed to fetch sub-services:", error.message);
      setServiceSubServicesMap(prev => ({
        ...prev,
        [serviceId]: []
      }));
    }
  };

  // Reset form function
  const resetForm = () => {
    setLeadStatus("");
    setSelectedServices([]);
    setServiceSubServicesMap({});
    setSelectedSubServicesMap({});
    setFollowUpDate("");
    setComment("");
    setLeadRemarks(""); // Reset lead remarks
    // Note: Don't reset callStatus here as it should maintain its value
  };

  // Submit handler
  const onSubmit = async () => {
    if (!callStatus) {
      return alert("Please select call status");
    }

    if (callStatus === "ANSWERED" && !leadStatus) {
      return alert("Please select lead status");
    }

    if (leadStatus === "FOLLOW_UP_NEEDED" && !followUpDate) {
      return alert("Please select follow up date");
    }

    if (leadStatus === "INTERESTED" && selectedServices.length === 0) {
      return alert("Please select at least one service");
    }

    // Validate lead remarks when services are selected
    if (leadStatus === "INTERESTED" && selectedServices.length > 0 && !leadRemarks) {
      return alert("Please select lead remarks");
    }

    // Validate that each selected service has at least one sub-service selected
    if (leadStatus === "INTERESTED") {
      const invalidServices = selectedServices.filter(serviceId => {
        const subServices = serviceSubServicesMap[serviceId] || [];
        const selectedSubServices = selectedSubServicesMap[serviceId] || [];
        return subServices.length > 0 && selectedSubServices.length === 0;
      });

      if (invalidServices.length > 0) {
        return alert("Please select sub-services for all selected services");
      }
    }

    setIsSubmitting(true);

    let payload = {
      userId: employeeId,
      leadId: selectedLeadId,
      callStatus,
      comment,
    };

    if (callStatus === "ANSWERED") {
      payload.leadStatus = leadStatus;

      if (leadStatus === "FOLLOW_UP_NEEDED") {
        payload.nextFollowUpDate = followUpDate;
      }

      if (leadStatus === "INTERESTED" && selectedServices.length > 0) {
        payload.services = [];
        payload.leadRemarks = leadRemarks; // Add lead remarks to payload

        selectedServices.forEach(serviceId => {
          const selectedSubServices = selectedSubServicesMap[serviceId] || [];
          const availableSubServices = serviceSubServicesMap[serviceId] || [];

          if (availableSubServices.length > 0 && selectedSubServices.length > 0) {
            // Add each selected sub-service
            selectedSubServices.forEach(subServiceId => {
              payload.services.push({
                subServiceId: subServiceId
              });
            });
          }
        });
      }
    }

    try {
      console.log("Payload:", payload);
      const response = await updateLeadStatus(payload)

      if (response) {
        console.log("Lead status updated successfully:", response);
        await fetchLeads();
        handleCloseDialog();
        alert("Lead status updated successfully!");
      }
    }
    catch (error) {
      console.error("Failed to update lead status:", error.message);
      alert("Failed to update lead status: " + error.message);
    }
    finally {
      setIsSubmitting(false);
    }
  };

  // Fetch services on component mount
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await getAllServices();
        setServices(data);
        console.log("Fetched services:", data);
      } catch (error) {
        console.error("Failed to fetch services:", error.message);
      }
    };
    fetchServices();
  }, []);

  // Fetch leads
  const fetchLeads = async () => {
    try {
      const res = await fetchLeadsOfEmployee(employeeId, page, size);
      if (res?.leads) {
        setLeads(res.leads);
        setTotalPages(res.totalPages);
      }
    } catch (err) {
      console.error("Failed to fetch employee leads:", err.message);
    }
  };

  useEffect(() => {
    if (employeeId) {
      fetchLeads();
    }
  }, [page, size]);

  const handleOpenDialog = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    setSelectedLeadId(leadId);
    setSelectedLead(lead);
    setOpenDialog(true);
    console.log("Lead ID clicked:", leadId);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedLeadId(null);
    setSelectedLead(null);
    // Reset all form fields when closing dialog
    setCallStatus("");
    setLeadStatus("");
    setSelectedServices([]);
    setServiceSubServicesMap({});
    setSelectedSubServicesMap({});
    setFollowUpDate("");
    setComment("");
    setLeadRemarks(""); // Reset lead remarks
  };

  const handleServiceChange = (e) => {
    const newSelectedServices = typeof e.target.value === 'string'
      ? e.target.value.split(',')
      : e.target.value;

    setSelectedServices(newSelectedServices);

    // Reset lead remarks when services change
    if (newSelectedServices.length === 0) {
      setLeadRemarks("");
    }

    // Fetch sub-services for newly selected services
    newSelectedServices.forEach(serviceId => {
      if (!serviceSubServicesMap[serviceId]) {
        fetchSubServices(serviceId);
      }
    });

    // Clean up sub-service selections for unselected services
    const newSelectedSubServicesMap = {};
    newSelectedServices.forEach(serviceId => {
      if (selectedSubServicesMap[serviceId]) {
        newSelectedSubServicesMap[serviceId] = selectedSubServicesMap[serviceId];
      }
    });
    setSelectedSubServicesMap(newSelectedSubServicesMap);
  };

  const handleSubServiceChange = (serviceId, e) => {
    const newSelectedSubServices = typeof e.target.value === 'string'
      ? e.target.value.split(',')
      : e.target.value;

    setSelectedSubServicesMap(prev => ({
      ...prev,
      [serviceId]: newSelectedSubServices
    }));
  };

  // Remove service function
  const removeService = (serviceIdToRemove, event) => {
    // Prevent the dropdown from closing
    if (event) {
      event.stopPropagation();
    }

    const newSelectedServices = selectedServices.filter(id => id !== serviceIdToRemove);
    setSelectedServices(newSelectedServices);

    // Reset lead remarks if no services are selected
    if (newSelectedServices.length === 0) {
      setLeadRemarks("");
    }

    // Also remove the sub-services for this service
    const newSelectedSubServicesMap = { ...selectedSubServicesMap };
    delete newSelectedSubServicesMap[serviceIdToRemove];
    setSelectedSubServicesMap(newSelectedSubServicesMap);

    // Don't remove from serviceSubServicesMap - keep the fetched data for reselection
  };

  // Remove sub-service function
  const removeSubService = (serviceId, subServiceIdToRemove, event) => {
    // Prevent the dropdown from closing
    if (event) {
      event.stopPropagation();
    }

    const currentSubServices = selectedSubServicesMap[serviceId] || [];
    const newSubServices = currentSubServices.filter(id => id !== subServiceIdToRemove);

    setSelectedSubServicesMap(prev => ({
      ...prev,
      [serviceId]: newSubServices
    }));
  };

  return (
    <div>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary">
              <div className={classes.topHeader}>
                <div>
                  <h4 className={classes.cardTitleWhite}>My Leads</h4>
                  <p className={classes.cardCategoryWhite}>
                    Page {page + 1} of {totalPages}
                  </p>
                </div>
                <div style={{ display: "flex", gap: "12px" }}>
                  <Button
                    color="white"
                    size="sm"
                    disabled={page <= 0}
                    onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                  >
                    ⬅ Previous
                  </Button>
                  <Button
                    color="white"
                    size="sm"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((prev) => prev + 1)}
                  >
                    Next ➡
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardBody>
              <GridContainer spacing={3} style={{ marginBottom: "20px" }}>
                <GridItem xs={12} sm={6} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Page Size</InputLabel>
                    <Select
                      value={size}
                      onChange={(e) => {
                        setSize(e.target.value);
                        setPage(0);
                      }}
                      label="Page Size"
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                </GridItem>
              </GridContainer>

              <div style={{ overflowX: "auto" }}>
                <MuiTable>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeadCell}>Name</TableCell>
                      <TableCell className={classes.tableHeadCell}>Email</TableCell>
                      <TableCell className={classes.tableHeadCell}>Phone</TableCell>
                      <TableCell className={classes.tableHeadCell}>Address</TableCell>
                      <TableCell className={classes.tableHeadCell}>Source</TableCell>
                       <TableCell className={classes.tableHeadCell}>Call Status</TableCell>
                      <TableCell className={classes.tableHeadCell}>Lead Status</TableCell>
                      <TableCell className={classes.tableHeadCell}>Created At</TableCell>
                      <TableCell className={classes.tableHeadCell}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leads.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          No leads found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      leads.map((lead) => (
                        <TableRow key={lead.id}>
                          <TableCell>{lead.name || "-"}</TableCell>
                          <TableCell>{lead.email || "-"}</TableCell>
                          <TableCell>{lead.phoneNumber || "-"}</TableCell>
                          <TableCell>{lead.address || "-"}</TableCell>
                          <TableCell>{lead.source || "-"}</TableCell>
                          <TableCell>
                            <span className={classes.statusBadge}>
                              {lead.callStatus || "N/A"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={classes.statusBadge}>
                              {lead.leadStatus || "N/A"}
                            </span>
                          </TableCell>
                          <TableCell>
                            {new Date(lead.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Button
                              color="info"
                              size="sm"
                              onClick={() => handleOpenDialog(lead.id)}
                            >
                              Call & Update
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </MuiTable>
              </div>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          Update Lead Status
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {/* Lead Information Card */}
          {selectedLead && (
            <Box className={classes.leadInfo}>
              <Typography variant="subtitle2" color="textSecondary">
                Lead Information
              </Typography>
              <Typography variant="body2">
                <strong>{selectedLead.name || 'N/A'}</strong> • {selectedLead.phoneNumber || 'N/A'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {selectedLead.email || 'No email'} • ID: {selectedLeadId}
              </Typography>
            </Box>
          )}

          <TextField
            select
            label="Call Status"
            value={callStatus}
            onChange={(e) => {
              setCallStatus(e.target.value);
              // Only reset dependent fields when changing away from ANSWERED
              if (e.target.value !== "ANSWERED") {
                setLeadStatus("");
                setSelectedServices([]);
                setServiceSubServicesMap({});
                setSelectedSubServicesMap({});
                setFollowUpDate("");
                setLeadRemarks(""); // Reset lead remarks
                // Don't reset comment - user might want to keep it
              }
            }}
            fullWidth
            size="small"
            className={classes.compactFormField}
            required
          >
            {callStatusOptions.map((status) => (
              <MenuItem key={status} value={status}>
                {status.replaceAll("_", " ")}
              </MenuItem>
            ))}
          </TextField>

          {callStatus === "ANSWERED" && (
            <TextField
              select
              label="Lead Status"
              value={leadStatus}
              onChange={(e) => {
                setLeadStatus(e.target.value);
                if (e.target.value !== "INTERESTED") {
                  setSelectedServices([]);
                  setServiceSubServicesMap({});
                  setSelectedSubServicesMap({});
                  setLeadRemarks(""); // Reset lead remarks
                }
                if (e.target.value !== "FOLLOW_UP_NEEDED") {
                  setFollowUpDate("");
                }
              }}
              fullWidth
              size="small"
              className={classes.compactFormField}
              required
            >
              {leadStatusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status.replaceAll("_", " ")}
                </MenuItem>
              ))}
            </TextField>
          )}

          {leadStatus === "FOLLOW_UP_NEEDED" && (
            <TextField
              label="Follow Up Date"
              type="date"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
              fullWidth
              size="small"
              className={classes.compactFormField}
              InputLabelProps={{ shrink: true }}
              required
            />
          )}

          {leadStatus === "INTERESTED" && (
            <>
              <TextField
                select
                label="Select Services"
                value={selectedServices}
                onChange={handleServiceChange}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => (
                    <Box className={classes.multiSelectValue}>
                      {selected.map(serviceId => {
                        const service = services.find(s => s.id === serviceId);
                        return (
                          <Chip
                            key={serviceId}
                            label={service ? service.serviceName : serviceId}
                            size="small"
                            className={classes.serviceChip}
                            onDelete={(e) => removeService(serviceId, e)}
                            deleteIcon={<span>×</span>}
                          />
                        );
                      })}
                    </Box>
                  )
                }}
                fullWidth
                size="small"
                className={classes.compactFormField}
                required
              >
                {services.map((service) => (
                  <MenuItem key={service.id} value={service.id}>
                    {service.serviceName}
                  </MenuItem>
                ))}
              </TextField>

              {/* Display selected services as chips outside the dropdown for better visibility */}
              {selectedServices.length > 0 && (
                <Box style={{ marginTop: 8, marginBottom: 8 }}>
                  <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 4 }}>
                    Selected Services:
                  </Typography>
                  <Box className={classes.multiSelectValue}>
                    {selectedServices.map(serviceId => {
                      const service = services.find(s => s.id === serviceId);
                      return (
                        <Chip
                          key={serviceId}
                          label={service ? service.serviceName : serviceId}
                          size="small"
                          className={classes.serviceChip}
                          onDelete={(e) => removeService(serviceId, e)}
                          deleteIcon={<span style={{ fontSize: '18px', fontWeight: 'bold' }}>×</span>}
                          color="primary"
                          variant="outlined"
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Render sub-service dropdowns for each selected service */}
              {selectedServices.map(serviceId => {
                const service = services.find(s => s.id === serviceId);
                const subServices = serviceSubServicesMap[serviceId] || [];
                const selectedSubServices = selectedSubServicesMap[serviceId] || [];

                if (subServices.length === 0) return null;

                return (
                  <Box key={serviceId} className={classes.subServiceSection}>
                    <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: 'block' }}>
                      Sub-services for {service?.serviceName}
                    </Typography>
                    <TextField
                      select
                      label="Select Sub-Services"
                      value={selectedSubServices}
                      onChange={(e) => handleSubServiceChange(serviceId, e)}
                      SelectProps={{
                        multiple: true,
                        renderValue: (selected) => (
                          <Box className={classes.multiSelectValue}>
                            {selected.map(subServiceId => {
                              const subService = subServices.find(ss => ss.id === subServiceId);
                              return (
                                <Chip
                                  key={subServiceId}
                                  label={subService ? subService.subServiceName : subServiceId}
                                  size="small"
                                  className={classes.serviceChip}
                                  onDelete={(e) => removeSubService(serviceId, subServiceId, e)}
                                  deleteIcon={<span>×</span>}
                                />
                              );
                            })}
                          </Box>
                        )
                      }}
                      fullWidth
                      size="small"
                      className={classes.compactFormField}
                    >
                      {subServices.map((subService) => (
                        <MenuItem key={subService.id} value={subService.id}>
                          {subService.subServiceName}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Display selected sub-services as chips outside the dropdown */}
                    {selectedSubServices.length > 0 && (
                      <Box style={{ marginTop: 8 }}>
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 4 }}>
                          Selected Sub-Services:
                        </Typography>
                        <Box className={classes.multiSelectValue}>
                          {selectedSubServices.map(subServiceId => {
                            const subService = subServices.find(ss => ss.id === subServiceId);
                            return (
                              <Chip
                                key={subServiceId}
                                label={subService ? subService.subServiceName : subServiceId}
                                size="small"
                                className={classes.serviceChip}
                                onDelete={(e) => removeSubService(serviceId, subServiceId, e)}
                                deleteIcon={<span style={{ fontSize: '18px', fontWeight: 'bold' }}>×</span>}
                                color="secondary"
                                variant="outlined"
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}


                {/* Lead Remarks Dropdown - Show when services are selected */}
              {selectedServices.length > 0 && (
                <Box className={classes.leadRemarksSection}>
                  <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: 'block' }}>
                    Lead Remarks (Required)
                  </Typography>
                  <TextField
                    select
                    label="Select Lead Remarks"
                    value={leadRemarks}
                    onChange={(e) => setLeadRemarks(e.target.value)}
                    fullWidth
                    size="small"
                    className={classes.compactFormField}
                    required
                  >
                    {leadRemarksOptions.map((remark) => (
                      <MenuItem key={remark} value={remark}>
                        {remark}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              )}
            </>
          )}

          {/* <TextField
            label="Comment (Optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            rows={2}
            fullWidth
            size="small"
            className={classes.compactFormField}
            placeholder="Add any additional comments..."
          /> */}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={handleCloseDialog}
            color="transparent"
            disabled={isSubmitting}
            size="sm"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            color="primary"
            disabled={isSubmitting}
            size="sm"
          >
            {isSubmitting ? "Updating..." : "Update Lead"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}







































































































// import React, { useEffect, useState } from "react";
// import {
//   Table as MuiTable,
//   TableBody,
//   TableCell,
//   TableHead,
//   TableRow,
//   Select,
//   MenuItem,
//   InputLabel,
//   FormControl,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Typography,
//   TextField,
// } from "@material-ui/core";

// import Card from "components/Card/Card.js";
// import CardHeader from "components/Card/CardHeader.js";
// import CardBody from "components/Card/CardBody.js";
// import Button from "components/CustomButtons/Button.js";

// import GridContainer from "components/Grid/GridContainer.js";
// import GridItem from "components/Grid/GridItem.js";
// import { makeStyles } from "@material-ui/core/styles";
// import styles from "assets/jss/material-dashboard-react/views/dashboardStyle.js";
// import { fetchLeadsOfEmployee } from "views/Apis/AfterLogin";
// import { getAllServices } from "views/Apis/AfterLogin";
// import { updateLeadStatus } from "views/Apis/AfterLogin";
// import { getAllSubServicesBasedOnServiceId } from "views/Apis/AfterLogin";

// const callStatusOptions = [
//   "ANSWERED",
//   "NOT_ANSWERED",
//   "DISCONNECTING",
//   "NOT_REACHABLE",
//   "OUT_OF_NETWORK",
//   "WRONG_NUMBER",
// ];

// const leadStatusOptions = [
//   "INTERESTED",
//   "NOT_INTERESTED",
//   // 'COLD',
//   // 'HOT',
//   "FOLLOW_UP_NEEDED",
//   // 'CONVERTED',
//   // 'LOST',
//   // 'NOT_IN_PURVIEW',
// ];

// const useStyles = makeStyles((theme) => ({
//   ...styles,
//   tableHeadCell: {
//     fontWeight: "bold",
//     backgroundColor: "#e3f2fd",
//     color: "#0d47a1",
//   },
//   statusBadge: {
//     padding: "4px 8px",
//     borderRadius: "4px",
//     backgroundColor: "#f1f8e9",
//     color: "#558b2f",
//     fontSize: "12px",
//     fontWeight: 500,
//     textAlign: "center",
//     display: "inline-block",
//     minWidth: 60,
//   },
//   paginationContainer: {
//     display: "flex",
//     justifyContent: "space-between",
//     marginTop: theme.spacing(2),
//     alignItems: "center",
//   },
//   topHeader: {
//     display: "flex",
//     justifyContent: "space-between",
//     alignItems: "center",
//     flexWrap: "wrap",
//     gap: theme.spacing(2),
//   },
// }));

// export default function GetLeadsEmployeeView() {
//   const classes = useStyles();
//   const [leads, setLeads] = useState([]);
//   const [page, setPage] = useState(0);
//   const [size, setSize] = useState(10);
//   const [totalPages, setTotalPages] = useState(0);
//   const [openDialog, setOpenDialog] = useState(false);
//   const [selectedLeadId, setSelectedLeadId] = useState(null);
//   const employeeId = sessionStorage.getItem("userId");

//   const [services, setServices] = useState([]);
//   const [selectedService, setSelectedService] = useState("");

//   const [isSubmitting, setIsSubmitting] = useState(false);

//   const [callStatus, setCallStatus] = useState("");
//   const [leadStatus, setLeadStatus] = useState("");
//   const [comment, setComment] = useState("");
//   const [followUpDate, setFollowUpDate] = useState("");

//   const [subServices, setSubServices] = useState([]);
//   const [selectedSubService, setSelectedSubService] = useState("");

//   const fetchSubServices = async (serviceId) => {
//     try {
//       const data = await getAllSubServicesBasedOnServiceId(serviceId);
//       setSubServices(data);
//       console.log("Fetched sub-services:", data);
//     } catch (error) {
//       console.error("Failed to fetch sub-services:", error.message);
//       setSubServices([]);
//     }
//   };

//   const onSubmit = async () => {
//     if (!callStatus) {
//       return alert("Please select call status");
//     }

//     if (callStatus === "ANSWERED" && !leadStatus) {
//       return alert("Please select lead status");
//     }

//     if (leadStatus === "FOLLOW_UP_NEEDED" && !followUpDate) {
//       return alert("Please select follow up date");
//     }

//     if (leadStatus === "INTERESTED" && !selectedService) {
//       return alert("Please select a service");
//     }

//     if (leadStatus === "INTERESTED" && !selectedSubService) {
//       return alert("Please select a sub-service");
//     }

//     setIsSubmitting(true);

//     let payload = {
//       userId: employeeId,
//       leadId: selectedLeadId,
//       callStatus,
//       comment,
//     };

//     if (callStatus === "ANSWERED") {
//       payload.leadStatus = leadStatus;

//       if (leadStatus === "FOLLOW_UP_NEEDED") {
//         payload.nextFollowUpDate = followUpDate;
//       }

//       if (leadStatus === "INTERESTED" && selectedSubService) {
//         payload.services = [
//           {
//             subServiceId: selectedSubService,
//           },
//         ];
//       }
//     }

//     try {
//       console.log("Payload:", payload);
//       const response = true

//       if (response) {
//         console.log("Lead status updated successfully:", response);
//         await fetchLeads();
//         handleCloseDialog();

//         setCallStatus("");
//         setLeadStatus("");
//         setSelectedService("");
//         setFollowUpDate("");
//         setComment("");

//         alert("Lead status updated successfully!");
//       }
//     } catch (error) {
//       console.error("Failed to update lead status:", error.message);
//       alert("Failed to update lead status: " + error.message);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   useEffect(() => {
//     const fetchServices = async () => {
//       try {
//         const data = await getAllServices();
//         setServices(data);
//         console.log("Fetched services:", data);
//       } catch (error) {
//         console.error("Failed to fetch services:", error.message);
//       }
//     };
//     fetchServices();
//   }, []);

//   const fetchLeads = async () => {
//     try {
//       const res = await fetchLeadsOfEmployee(employeeId, page, size);
//       if (res?.leads) {
//         setLeads(res.leads);
//         setTotalPages(res.totalPages);
//       }
//     } catch (err) {
//       console.error("Failed to fetch employee leads:", err.message);
//     }
//   };

//   useEffect(() => {
//     if (employeeId) {
//       fetchLeads();
//     }
//   }, [page, size]);

//   const handleOpenDialog = (leadId) => {
//     setSelectedLeadId(leadId);
//     setOpenDialog(true);
//     console.log("Lead ID clicked:", leadId);
//   };

//   const handleCloseDialog = () => {
//     setOpenDialog(false);
//     setSelectedLeadId(null);
//   };

//   return (
//     <div>
//       <GridContainer>
//         <GridItem xs={12}>
//           <Card>
//             <CardHeader color="primary">
//               <div className={classes.topHeader}>
//                 <div>
//                   <h4 className={classes.cardTitleWhite}>My Leads</h4>
//                   <p className={classes.cardCategoryWhite}>
//                     Page {page + 1} of {totalPages}
//                   </p>
//                 </div>
//                 <div style={{ display: "flex", gap: "12px" }}>
//                   <Button
//                     color="white"
//                     size="sm"
//                     disabled={page <= 0}
//                     onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
//                   >
//                     ⬅ Previous
//                   </Button>
//                   <Button
//                     color="white"
//                     size="sm"
//                     disabled={page >= totalPages - 1}
//                     onClick={() => setPage((prev) => prev + 1)}
//                   >
//                     Next ➡
//                   </Button>
//                 </div>
//               </div>
//             </CardHeader>

//             <CardBody>
//               <GridContainer spacing={3} style={{ marginBottom: "20px" }}>
//                 <GridItem xs={12} sm={6} md={3}>
//                   <FormControl fullWidth variant="outlined">
//                     <InputLabel>Page Size</InputLabel>
//                     <Select
//                       value={size}
//                       onChange={(e) => {
//                         setSize(e.target.value);
//                         setPage(0);
//                       }}
//                       label="Page Size"
//                     >
//                       <MenuItem value={5}>5</MenuItem>
//                       <MenuItem value={10}>10</MenuItem>
//                       <MenuItem value={20}>20</MenuItem>
//                       <MenuItem value={50}>50</MenuItem>
//                       <MenuItem value={100}>100</MenuItem>
//                     </Select>
//                   </FormControl>
//                 </GridItem>
//               </GridContainer>

//               <div style={{ overflowX: "auto" }}>
//                 <MuiTable>
//                   <TableHead>
//                     <TableRow>
//                       <TableCell className={classes.tableHeadCell}>
//                         Name
//                       </TableCell>
//                       <TableCell className={classes.tableHeadCell}>
//                         Email
//                       </TableCell>
//                       <TableCell className={classes.tableHeadCell}>
//                         Phone
//                       </TableCell>
//                       <TableCell className={classes.tableHeadCell}>
//                         Address
//                       </TableCell>
//                       <TableCell className={classes.tableHeadCell}>
//                         Source
//                       </TableCell>
//                       <TableCell className={classes.tableHeadCell}>
//                         Lead Status
//                       </TableCell>
//                       <TableCell className={classes.tableHeadCell}>
//                         Created At
//                       </TableCell>
//                       <TableCell className={classes.tableHeadCell}>
//                         Action
//                       </TableCell>
//                     </TableRow>
//                   </TableHead>
//                   <TableBody>
//                     {leads.length === 0 ? (
//                       <TableRow>
//                         <TableCell colSpan={8} align="center">
//                           No leads found.
//                         </TableCell>
//                       </TableRow>
//                     ) : (
//                       leads.map((lead) => (
//                         <TableRow key={lead.id}>
//                           <TableCell>{lead.name || "-"}</TableCell>
//                           <TableCell>{lead.email || "-"}</TableCell>
//                           <TableCell>{lead.phoneNumber || "-"}</TableCell>
//                           <TableCell>{lead.address || "-"}</TableCell>
//                           <TableCell>{lead.source || "-"}</TableCell>
//                           <TableCell>
//                             <span className={classes.statusBadge}>
//                               {lead.leadStatus || "N/A"}
//                             </span>
//                           </TableCell>
//                           <TableCell>
//                             {new Date(lead.createdAt).toLocaleDateString()}
//                           </TableCell>
//                           <TableCell>
//                             <Button
//                               color="info"
//                               size="sm"
//                               onClick={() => handleOpenDialog(lead.id)}
//                             >
//                               Call & Update
//                             </Button>
//                           </TableCell>
//                         </TableRow>
//                       ))
//                     )}
//                   </TableBody>
//                 </MuiTable>
//               </div>
//             </CardBody>
//           </Card>
//         </GridItem>
//       </GridContainer>

//       <Dialog open={openDialog} onClose={handleCloseDialog}>
//         <DialogTitle>Call Status</DialogTitle>
//         <DialogContent>
//           <Typography variant="body1">
//             You clicked on lead ID: <strong>{selectedLeadId}</strong>
//           </Typography>

//           <TextField
//             select
//             label="Call Status"
//             value={callStatus}
//             onChange={(e) => {
//               setCallStatus(e.target.value);
//               if (e.target.value !== "ANSWERED") setLeadStatus("");
//             }}
//             fullWidth
//             margin="normal"
//           >
//             {callStatusOptions.map((status) => (
//               <MenuItem key={status} value={status}>
//                 {status.replaceAll("_", " ")}
//               </MenuItem>
//             ))}
//           </TextField>

//           {callStatus === "ANSWERED" && (
//             <TextField
//               select
//               label="Lead Status"
//               value={leadStatus}
//               onChange={(e) => setLeadStatus(e.target.value)}
//               fullWidth
//               margin="normal"
//             >
//               {leadStatusOptions.map((status) => (
//                 <MenuItem key={status} value={status}>
//                   {status.replaceAll("_", " ")}
//                 </MenuItem>
//               ))}
//             </TextField>
//           )}

//           {leadStatus === "FOLLOW_UP_NEEDED" && (
//             <TextField
//               label="Follow Up Date"
//               type="date"
//               value={followUpDate}
//               onChange={(e) => setFollowUpDate(e.target.value)}
//               fullWidth
//               margin="normal"
//               InputLabelProps={{ shrink: true }}
//             />
//           )}

//           {leadStatus === "INTERESTED" && (
//             <>
//               <TextField
//                 select
//                 label="Select Service"
//                 value={selectedService}
//                 onChange={(e) => {
//                   const serviceId = e.target.value;
//                   setSelectedService(serviceId);
//                   setSelectedSubService(""); // Reset sub-service selection
//                   if (serviceId) {
//                     fetchSubServices(serviceId); // Fetch sub-services when service is selected
//                   } else {
//                     setSubServices([]); // Clear sub-services if no service selected
//                   }
//                 }}
//                 fullWidth
//                 margin="normal"
//               >
//                 {services.map((service) => (
//                   <MenuItem key={service.id} value={service.id}>
//                     {service.serviceName}
//                   </MenuItem>
//                 ))}
//               </TextField>

//               {/* Sub-service dropdown - only show if service is selected and sub-services exist */}
//               {selectedService && subServices.length > 0 && (
//                 <TextField
//                   select
//                   label="Select Sub Service"
//                   value={selectedSubService}
//                   onChange={(e) => setSelectedSubService(e.target.value)}
//                   fullWidth
//                   margin="normal"
//                 >
//                   {subServices.map((subService) => (
//                     <MenuItem key={subService.id} value={subService.id}>
//                       {subService.subServiceName}
//                     </MenuItem>
//                   ))}
//                 </TextField>
//               )}
//             </>
//           )}

//           {/* <TextField
//             label="Comment"
//             value={comment}
//             onChange={(e) => setComment(e.target.value)}
//             multiline
//             rows={3}
//             fullWidth
//             margin="normal"
//           /> */}
//         </DialogContent>

//         <DialogActions>
//           <Button
//             onClick={handleCloseDialog}
//             color="error"
//             disabled={isSubmitting}
//           >
//             Cancel
//           </Button>
//           <Button onClick={onSubmit} color="primary" disabled={isSubmitting}>
//             {isSubmitting ? "Submitting..." : "Submit"}
//           </Button>
//         </DialogActions>
//       </Dialog>
//     </div>
//   );
// }